import {
  Alert,
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  EmptyState,
  Input,
  Skeleton,
  Text,
} from "@assetwatch/ui";
import { useState } from "react";
import type { WorkOrderId } from "../../lib/workIds";
import {
  type ProcedureAttachment,
  useAttachProcedureMutation,
} from "../../queries/useProcedureAttachment";
import { useProceduresQuery } from "../../queries/useProceduresQuery";

function getAttachmentTitle(attachment: ProcedureAttachment): string | null {
  const { snapshot } = attachment;
  if (
    typeof snapshot !== "object" ||
    snapshot === null ||
    !("title" in snapshot)
  ) {
    return null;
  }
  return typeof snapshot.title === "string" ? snapshot.title : null;
}

export function ProcedureAttachmentPicker({
  attachment,
  canAttach,
  isEditing = false,
  loading = false,
  onProcedureChange,
  selectedProcedureId,
  workOrderId,
}: {
  attachment: ProcedureAttachment | null;
  canAttach: boolean;
  isEditing?: boolean;
  loading?: boolean;
  onProcedureChange?: (procedureId: string | null) => void;
  selectedProcedureId?: string | null;
  workOrderId: WorkOrderId;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [stagedProcedure, setStagedProcedure] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const proceduresQuery = useProceduresQuery(search);
  const attachMutation = useAttachProcedureMutation(workOrderId);
  const procedures = (proceduresQuery.data?.items ?? []).filter(
    (procedure) => procedure.status === "published",
  );
  const selectedProcedure = procedures.find(
    (procedure) => procedure.id === selectedProcedureId,
  );

  if (loading && !attachment) {
    return (
      <div className="flex flex-col gap-(--spacing-12)">
        <Skeleton height={24} width="45%" />
        <Skeleton height={18} width="80%" />
        <Skeleton height={40} width="100%" />
        <Text tone="subtle">Loading procedure…</Text>
      </div>
    );
  }

  if (attachment && !isEditing) return null;

  if (!canAttach && !isEditing) {
    return (
      <EmptyState
        description="This work order has no procedure linked yet. Attach an existing procedure so the technician has step-by-step instructions to follow."
        headingLevel={3}
        icon="checklist"
        title="No procedure attached"
      />
    );
  }

  async function attachProcedure(procedureId: string) {
    try {
      await attachMutation.mutateAsync(procedureId);
      setDrawerOpen(false);
      setSearch("");
    } catch {
      // The mutation error is rendered in the drawer for an actionable retry.
    }
  }

  function chooseProcedure(procedureId: string, procedureName: string) {
    if (isEditing) {
      setStagedProcedure({ id: procedureId, name: procedureName });
      onProcedureChange?.(procedureId);
      setDrawerOpen(false);
      setSearch("");
      return;
    }
    void attachProcedure(procedureId);
  }

  return (
    <>
      {isEditing ? (
        selectedProcedureId ? (
          <div className="flex flex-wrap items-center justify-between gap-(--spacing-12)">
            <div className="flex min-w-0 flex-col">
              <Text>
                {attachment?.procedureId === selectedProcedureId
                  ? (getAttachmentTitle(attachment) ??
                    selectedProcedure?.name ??
                    selectedProcedureId)
                  : (selectedProcedure?.name ??
                    (stagedProcedure?.id === selectedProcedureId
                      ? stagedProcedure.name
                      : undefined) ??
                    selectedProcedureId)}
              </Text>
              <Text size="sm" tone="subtle">
                {attachment?.procedureId === selectedProcedureId
                  ? `Version ${attachment.procedureVersion} · current attachment`
                  : "Staged until Save"}
              </Text>
            </div>
            <div className="ml-auto flex gap-(--spacing-8)">
              <Button
                onClick={() => setDrawerOpen(true)}
                size="sm"
                variant="outline"
              >
                Change procedure
              </Button>
              <Button
                intent="destructive"
                onClick={() => {
                  setStagedProcedure(null);
                  onProcedureChange?.(null);
                }}
                size="sm"
                variant="ghost"
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <EmptyState
            actions={
              <Button onClick={() => setDrawerOpen(true)} size="sm">
                Add procedure
              </Button>
            }
            description="Choose a published procedure. Your change is applied when you save the Work Order."
            headingLevel={3}
            icon="checklist"
            title="No procedure attached"
          />
        )
      ) : (
        <EmptyState
          actions={
            <Button onClick={() => setDrawerOpen(true)} size="sm">
              Add procedure
            </Button>
          }
          description="This work order has no procedure linked yet. Attach an existing procedure so the technician has step-by-step instructions to follow."
          headingLevel={3}
          icon="checklist"
          title="No procedure attached"
        />
      )}

      {isEditing &&
      attachment?.snapshot === null &&
      selectedProcedureId === attachment.procedureId ? (
        <Alert
          title="The attached Procedure steps are unavailable. Replace or remove this Procedure to continue."
          tone="negative"
        />
      ) : null}

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} responsive={false}>
        <DrawerContent size="small">
          <DrawerHeader title="Add procedure" />
          <DrawerBody gap="none">
            <div className="-mx-(--spacing-20) border-b border-outline-subtle px-(--spacing-20) py-(--spacing-12)">
              <Input
                autoFocus
                leadingIcon="search"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search procedures"
                reserveSubtextSpace={false}
                size="sm"
                value={search}
              />
            </div>
            <div className="-mx-(--spacing-20) flex min-h-0 flex-1 flex-col overflow-y-auto">
              {attachMutation.error ? (
                <div className="px-(--spacing-20) pt-(--spacing-16)">
                  <Alert
                    subtext={attachMutation.error.message}
                    title="Unable to attach procedure"
                    tone="negative"
                  />
                </div>
              ) : null}
              {proceduresQuery.isPending ? (
                <div className="px-(--spacing-20) py-(--spacing-32) text-center">
                  <Text tone="subtle">Loading procedures…</Text>
                </div>
              ) : proceduresQuery.isError ? (
                <div className="px-(--spacing-20) pt-(--spacing-16)">
                  <Alert
                    subtext="Try again or contact an administrator if the problem continues."
                    title="Unable to load procedures"
                    tone="negative"
                  />
                </div>
              ) : procedures.length === 0 ? (
                <div className="px-(--spacing-20) py-(--spacing-32) text-center">
                  <Text tone="subtle">No procedures match your search.</Text>
                </div>
              ) : (
                procedures.map((procedure) => (
                  <button
                    className="flex w-full cursor-pointer flex-col items-start gap-(--spacing-4) border-0 border-b border-outline-subtle bg-transparent px-(--spacing-20) py-(--spacing-12) text-left hover:bg-surface-subtle disabled:cursor-default"
                    disabled={attachMutation.isPending}
                    key={procedure.id}
                    onClick={() =>
                      chooseProcedure(procedure.id, procedure.name)
                    }
                    type="button"
                  >
                    <span className="font-(number:--font-weight-medium) text-text-default">
                      {procedure.name}
                    </span>
                    <span className="text-(length:--font-size-body-small) text-text-subtext">
                      {procedure.id} · {procedure.owner ?? "Unassigned"} · v
                      {procedure.currentVersion ?? "—"}
                    </span>
                    <Chip label="Published" tone="positive" />
                  </button>
                ))
              )}
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
